// ──────────────────────────────────────────────────────────────────────────────
// useBreakfastMutations — thin compatibility barrel
//
// The original 1,274-line implementation of this module was split into
// per-mutation hooks under `src/features/breakfast/` in the PR-2 refactor:
//
//   src/features/breakfast/
//     types.ts                        // shared interfaces
//     queryKeys.ts                    // centralised React Query keys
//     hooks/
//       useBreakfastEvents.ts         // calendar-visible events
//       usePendingBreakfastInvites.ts // RSVP Center invites
//     mutations/
//       useCreateEvent.ts             // create event + fan-out
//       useUpdateEventFull.ts         // composite save
//       useDeleteEvent.ts             // host-only delete
//       useRsvp.ts                    // useRespondRsvp + useSetMyRsvp
//       usePotluckItems.ts            // claim + add + remove potluck
//       useEventAttendees.ts          // addAttendees + removeEventInvite
//
// This file stays at its historical import path (`@/hooks/useBreakfastMutations`)
// so every existing consumer — Dashboard, NotificationBell, BreakfastModal,
// BreakfastEventList, PendingInvitations, Circles, BreakfastSummaryCard —
// keeps compiling untouched. Two surfaces are preserved verbatim:
//
//   1. All type exports (`BreakfastEvent`, `BreakfastAttendee`,
//      `PotluckItem`, `BreakfastEventWithAttendees`, `PendingBreakfastInvite`)
//      and the two query hooks (`useBreakfastEvents`,
//      `usePendingBreakfastInvites`) are re-exported one-to-one.
//
//   2. `useBreakfastMutations()` still returns the same object shape:
//          { createEvent, respondRsvp, setMyRsvp, deleteEvent,
//            updateEventFull, claimPotluckItem, addPotluckItem,
//            removePotluckItem, addAttendees, removeEventInvite }
//      It composes the individual hooks so callers can keep destructuring
//      whichever slice they need. New code should prefer importing the
//      specific hook directly from `src/features/breakfast/mutations/*`
//      to keep dependency graphs tight.
// ──────────────────────────────────────────────────────────────────────────────

export type {
  BreakfastEvent,
  BreakfastAttendee,
  PotluckItem,
  BreakfastEventWithAttendees,
  PendingBreakfastInvite,
} from "@/features/breakfast/types";

export { useBreakfastEvents } from "@/features/breakfast/hooks/useBreakfastEvents";
export { usePendingBreakfastInvites } from "@/features/breakfast/hooks/usePendingBreakfastInvites";

import { useCreateEvent } from "@/features/breakfast/mutations/useCreateEvent";
import { useUpdateEventFull } from "@/features/breakfast/mutations/useUpdateEventFull";
import { useDeleteEvent } from "@/features/breakfast/mutations/useDeleteEvent";
import {
  useRespondRsvp,
  useSetMyRsvp,
} from "@/features/breakfast/mutations/useRsvp";
import {
  useClaimPotluckItem,
  useAddPotluckItem,
  useRemovePotluckItem,
} from "@/features/breakfast/mutations/usePotluckItems";
import {
  useAddAttendees,
  useRemoveEventInvite,
} from "@/features/breakfast/mutations/useEventAttendees";

// Re-exports for new code that wants a specific hook without composing the
// whole bundle.
export {
  useCreateEvent,
  useUpdateEventFull,
  useDeleteEvent,
  useRespondRsvp,
  useSetMyRsvp,
  useClaimPotluckItem,
  useAddPotluckItem,
  useRemovePotluckItem,
  useAddAttendees,
  useRemoveEventInvite,
};

/**
 * Composite hook preserved for backwards compatibility. Every consumer that
 * does `const { createEvent, deleteEvent } = useBreakfastMutations()` keeps
 * working untouched.
 *
 * Each slot is a real `UseMutationResult` returned by its own hook, so all
 * the React Query ergonomics (`.mutate`, `.mutateAsync`, `.isPending`,
 * `.reset`, …) behave exactly as before.
 */
export function useBreakfastMutations() {
  const createEvent = useCreateEvent();
  const respondRsvp = useRespondRsvp();
  const setMyRsvp = useSetMyRsvp();
  const deleteEvent = useDeleteEvent();
  const updateEventFull = useUpdateEventFull();
  const claimPotluckItem = useClaimPotluckItem();
  const addPotluckItem = useAddPotluckItem();
  const removePotluckItem = useRemovePotluckItem();
  const addAttendees = useAddAttendees();
  const removeEventInvite = useRemoveEventInvite();

  return {
    createEvent,
    respondRsvp,
    setMyRsvp,
    deleteEvent,
    updateEventFull,
    claimPotluckItem,
    addPotluckItem,
    removePotluckItem,
    addAttendees,
    removeEventInvite,
  };
}
